import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMapMarkerAlt, faClock, 
    faPlane, faFilePdf, faCheckCircle, faTimesCircle, 
    faHotel, faPlaneDeparture, faPlaneArrival, faComment
} from '@fortawesome/free-solid-svg-icons';

const FALLBACK_IMAGE = "https://trawtelcdn.s3.eu-north-1.amazonaws.com/smarttravelcrm/package_images/default-package.jpg";

// Helper to format date/time
const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper to calculate nights between two dates
const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

const DMCPackageDetailsForAgents = () => {
    const { id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    
    const [pkg, setPkg] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    // Enquiry state
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [enquiryData, setEnquiryData] = useState({
        checkIn: "",
        checkOut: "",
        adults: 1,
        children: 0,
        nights: 0,
        customerName: "",
        customerEmail: "",
        customerPhone: "",
        specialRequests: ""
    });
    const [isSubmittingEnquiry, setIsSubmittingEnquiry] = useState(false);

    const fetchPackageDetails = async () => {
        setIsLoading(true);
        try {
            const res = await apiService.getAll(`${config.GETDMCPACKAGEBYID}/${id}`);
            console.log("Fetched Package Details:", res);
            if (res) {
                setPkg(res);
                setSelectedPackage(res);
            }
        } catch (error) {
            console.error("Error fetching package details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && !user) {
            navigate("/");
        } else if (id) {
            fetchPackageDetails();
        }
    }, [id, user, authLoading]);

    // Update nights when dates change
    useEffect(() => {
        if (enquiryData.checkIn && enquiryData.checkOut) {
            const nights = calculateNights(enquiryData.checkIn, enquiryData.checkOut);
            setEnquiryData(prev => ({
                ...prev,
                nights: nights
            }));
        }
    }, [enquiryData.checkIn, enquiryData.checkOut]);

    // Open enquiry modal
    const openEnquiryModal = () => {
        if (!selectedPackage) return;
        
        // Set default dates (tomorrow and day after tomorrow)
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayAfterTomorrow = new Date();
        dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
        
        const initialNights = calculateNights(
            tomorrow.toISOString().split('T')[0],
            dayAfterTomorrow.toISOString().split('T')[0]
        );
        
        setEnquiryData({
            checkIn: tomorrow.toISOString().split('T')[0],
            checkOut: dayAfterTomorrow.toISOString().split('T')[0],
            adults: 1,
            children: 0,
            nights: initialNights,
            customerName: user?.name || "",
            customerEmail: user?.email || "",
            customerPhone: user?.phoneNumber || "",
            specialRequests: ""
        });
        
        setShowEnquiryModal(true);
    };

    // Close enquiry modal
    const closeEnquiryModal = () => {
        setShowEnquiryModal(false);
        setEnquiryData({
            checkIn: "",
            checkOut: "",
            adults: 1,
            children: 0,
            nights: 0,
            customerName: "",
            customerEmail: "",
            customerPhone: "",
            specialRequests: ""
        });
        setIsSubmittingEnquiry(false);
    };

    // Handle enquiry form submission
    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingEnquiry(true);
        
        if (!selectedPackage || !user) {
            alert("Please select a package first.");
            setIsSubmittingEnquiry(false);
            return;
        }
        
        // Validate required fields
        if (!enquiryData.checkIn || !enquiryData.checkOut || 
            !enquiryData.customerName || !enquiryData.customerEmail || !enquiryData.customerPhone) {
            alert("Please fill in all required fields: Travel Date, Return Date, Name, Email, and Phone.");
            setIsSubmittingEnquiry(false);
            return;
        }
        
        // Validate dates
        const checkInDate = new Date(enquiryData.checkIn);
        const checkOutDate = new Date(enquiryData.checkOut);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (checkInDate < today) {
            alert("Travel date cannot be in the past.");
            setIsSubmittingEnquiry(false);
            return;
        }
        
        if (checkOutDate <= checkInDate) {
            alert("Return date must be after travel date.");
            setIsSubmittingEnquiry(false);
            return;
        }
        
        try {
            // Create the Lead request object
            const leadRequest = {
                leadId: "00000000-0000-0000-0000-000000000000",
                senderId: user.id,
                senderStatus: "Pending",
                senderTime: new Date().toISOString(),
                receiverId: selectedPackage.userId,
                receiverStatus: "Pending",
                receiverTime: null,
                
                // ServiceDetails - Package enquiry data
                serviceDetails: {
                    // User entered booking details
                    checkIn: enquiryData.checkIn,
                    checkOut: enquiryData.checkOut,
                    adults: enquiryData.adults,
                    children: enquiryData.children,
                    nights: enquiryData.nights,
                    totalGuests: parseInt(enquiryData.adults) + parseInt(enquiryData.children),
                    
                    // User entered special requests
                    specialRequests: enquiryData.specialRequests || "",
                    
                    // Package reference
                    packageName: selectedPackage.title,
                    packageId: selectedPackage.id,
                    totalNights: selectedPackage.totalNights,
                    totalDays: selectedPackage.totalDays,
                    destinations: selectedPackage.destinations
                },
                
                // SenderDetails (current user's info)
                senderDetails: {
                    userId: user.id,
                    companyName: user.companyname,
                    senderName: user.name,
                    senderEmail: user.email,
                    senderPhone: user.phoneNumber
                },
                
                // ReceiverDetails (minimal DMC info)
                receiverDetails: {
                    dmcName: selectedPackage.companyName,
                    ownerEmail: selectedPackage.companyEmail || "",
                    ownerPhone: selectedPackage.companyPhone || ""
                },
                
                // Customer contact info (from user input)
                name: enquiryData.customerName,
                email: enquiryData.customerEmail,
                phoneNumber: enquiryData.customerPhone,
                
                // General lead info
                leadStatus: "NEW",
                sourceName: "B2B Portal",
                enquiryType: "PACKAGE",
                referenceId: selectedPackage.id,
                description: `Package enquiry for ${selectedPackage.title} from ${enquiryData.checkIn} to ${enquiryData.checkOut} (${enquiryData.nights} nights)`,
                callBack: null,
                
                // System fields
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString()
            };
            
            // Call API to create lead
            const response = await apiService.post(
                `${config.CREATELEAD}`,
                leadRequest
            );
            
            if (response.success || response.data) {
                alert("Enquiry submitted successfully! A lead has been created.");
                closeEnquiryModal();
            } else {
                throw new Error("Failed to submit enquiry and create lead");
            }
            
        } catch (error) {
            console.error("Error submitting enquiry:", error);
            alert("Failed to submit enquiry. Please try again.");
        } finally {
            setIsSubmittingEnquiry(false);
        }
    };

    // Handle enquiry form change
    const handleEnquiryChange = (e) => {
        const { name, value } = e.target;
        setEnquiryData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle number input change
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numValue = parseInt(value) || 0;
        setEnquiryData(prev => ({
            ...prev,
            [name]: numValue
        }));
    };

    if (authLoading || isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (!pkg) return <div className="text-center mt-5"><h3>Package Not Found</h3></div>;

    // Logic for Image
    const displayImage = (pkg.media?.images && pkg.media.images.length > 0) 
        ? pkg.media.images[0].url 
        : FALLBACK_IMAGE;

    // Logic for PDF
    const pdfUrl = pkg.media?.itineraryPdf?.url;

    return (
        <div style={{ background: '#f8f9fa', minHeight: '100vh', paddingBottom: '50px' }}>
            <Header User={user} activeSection="holidays" />
            
            <div className="container mt-4">
                {/* Send Enquiry Button - Only Button */}
                {/* <div className="d-flex justify-content-end mb-4">
                    <button 
                        className="btn btn-success" 
                        onClick={openEnquiryModal}
                        style={{ backgroundColor: '#28a745', borderColor: '#28a745', padding: '10px 20px' }}
                    >
                        <FontAwesomeIcon icon={faComment} className="me-2" /> Send Enquiry
                    </button>
                </div> */}
<div className="d-flex justify-content-end mb-4">
    <button 
        className="btn btn-secondary" 
        onClick={() => window.history.back()}  // or your back function
        style={{ padding: '10px 20px' }}
    >
         Back
    </button>
</div>
                <div className="row">
                    {/* LEFT COLUMN - MAIN CONTENT */}
                    <div className="col-lg-8">
                        
                        {/* Hero Image */}
                        <div className="card shadow-sm border-0 mb-4 overflow-hidden" style={{ borderRadius: '12px' }}>
                            <div style={{ height: '350px', position: 'relative' }}>
                                <img 
                                    src={displayImage} 
                                    alt={pkg.title} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                                />
                                <div style={{ 
                                    position: 'absolute', bottom: 0, left: 0, right: 0, 
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', 
                                    padding: '20px', color: 'white' 
                                }}>
                                    <h2 className="fw-bold mb-1 text-white">{pkg.title}</h2>
                                    <div className="d-flex align-items-center gap-3 text-light opacity-75">
                                        <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {pkg.destinations?.[0]?.countryName || "DMC Package"}</span>
                                        <span><FontAwesomeIcon icon={faClock} /> {pkg.totalNights} Nights / {pkg.totalDays} Days</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Description & Overview */}
                        <div className="card shadow-sm border-0 mb-4 p-4" style={{ borderRadius: '12px' }}>
                            <h5 className="fw-bold text-primary mb-3">Package Overview</h5>
                            <p className="text-muted" style={{ whiteSpace: 'pre-line' }}>{pkg.description || "No description provided."}</p>
                            
                            {pkg.contentType === 2 && (
                                <div className="alert alert-light border-primary d-flex align-items-center mt-3">
                                    <FontAwesomeIcon icon={faFilePdf} className="text-danger fa-2x me-3" />
                                    <div className="flex-grow-1">
                                        <h6 className="mb-0 fw-bold">Download Package Flyer</h6>
                                        <small className="text-muted">This package is a PDF-based itinerary.</small>
                                    </div>
                                    {pdfUrl ? (
                                        <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-danger">Download PDF</a>
                                    ) : (
                                        <span className="text-muted small">No PDF Attached</span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Flight Details (If Included) */}
                        {pkg.flights?.included && pkg.flights.options?.length > 0 && (
                            <div className="card shadow-sm border-0 mb-4 p-4" style={{ borderRadius: '12px' }}>
                                <h5 className="fw-bold text-primary mb-3"><FontAwesomeIcon icon={faPlane} /> Flight Details</h5>
                                {pkg.flights.options.map((opt, i) => (
                                    <div key={i} className="mb-3 border rounded p-3 bg-light">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{opt.airline} <span className="badge bg-secondary ms-2">{opt.cabin}</span></strong>
                                        </div>
                                        {opt.segments?.map((seg, j) => (
                                            <div key={j} className="d-flex align-items-center justify-content-between small text-muted border-top pt-2 mt-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="fw-bold text-dark">{seg.flightNo}</span>
                                                    <span>{seg.from} <FontAwesomeIcon icon={faPlaneDeparture} /></span>
                                                    <span>{formatTime(seg.departureTime)}</span>
                                                </div>
                                                <div className="text-center" style={{flex: 1, borderBottom: '1px dashed #ccc', margin: '0 10px'}}></div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span>{formatTime(seg.arrivalTime)}</span>
                                                    <span><FontAwesomeIcon icon={faPlaneArrival} /> {seg.to}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Accommodation / Hotels */}
                        {pkg.destinations?.length > 0 && (
                            <div className="card shadow-sm border-0 mb-4 p-4" style={{ borderRadius: '12px' }}>
                                <h5 className="fw-bold text-primary mb-3"><FontAwesomeIcon icon={faHotel} /> Accommodation</h5>
                                {pkg.destinations.map((dest, i) => (
                                    <div key={i} className="mb-3">
                                        <h6 className="fw-bold text-dark border-bottom pb-2">{dest.countryName}</h6>
                                        {dest.places?.map((place, j) => (
                                            <div key={j} className="ms-3 mb-3">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span className="badge bg-info">{place.cityName}</span>
                                                    <span className="text-muted small">({place.nights} Nights)</span>
                                                </div>
                                                {place.hotels?.length > 0 ? (
                                                    <div className="row g-2">
                                                        {place.hotels.map((hotel, k) => (
                                                            <div key={k} className="col-12">
                                                                {hotel.hotelOptions?.map((opt, l) => (
                                                                    <div key={l} className="d-flex align-items-center justify-content-between bg-white border rounded p-2">
                                                                        <div>
                                                                            <strong>{opt.name || "Hotel Name TBD"}</strong>
                                                                            <div className="small text-muted">{opt.roomType} | {opt.mealPlan}</div>
                                                                        </div>
                                                                        <div className="text-warning">
                                                                            {"★".repeat(hotel.hotelCategory || 3)}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : <p className="small text-muted fst-italic">No specific hotels listed.</p>}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Day-by-Day Itinerary */}
                        {pkg.contentType === 1 && pkg.itinerary && pkg.itinerary.length > 0 && (
                            <div className="card shadow-sm border-0 mb-4 p-4" style={{ borderRadius: '12px' }}>
                                <h5 className="fw-bold text-primary mb-4"><FontAwesomeIcon icon={faMapMarkerAlt} /> Itinerary</h5>
                                <div className="timeline">
                                    {pkg.itinerary.map((day, index) => (
                                        <div key={index} className="d-flex mb-4">
                                            <div className="me-3 text-center">
                                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold" 
                                                     style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    {day.day}
                                                </div>
                                                {index !== pkg.itinerary.length - 1 && <div style={{ width: '2px', height: '100%', background: '#e9ecef', margin: '0 auto' }}></div>}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className="fw-bold mb-1">Day {day.day}: {day.title}</h6>
                                                {day.cityName && <small className="text-muted d-block mb-2"><FontAwesomeIcon icon={faMapMarkerAlt} /> {day.cityName}</small>}
                                                <p className="text-muted small mb-0">{day.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Inclusions & Exclusions */}
                        {(pkg.inclusions?.length > 0 || pkg.exclusions?.length > 0) && (
                            <div className="row">
                                <div className="col-md-6 mb-4">
                                    <div className="card shadow-sm border-0 h-100 p-4" style={{ borderRadius: '12px' }}>
                                        <h6 className="fw-bold text-success mb-3"><FontAwesomeIcon icon={faCheckCircle} /> Inclusions</h6>
                                        <ul className="list-unstyled mb-0">
                                            {pkg.inclusions?.map((inc, i) => (
                                                <li key={i} className="mb-2 text-muted small d-flex align-items-start">
                                                    <span className="me-2">✓</span> {inc}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-4">
                                    <div className="card shadow-sm border-0 h-100 p-4" style={{ borderRadius: '12px' }}>
                                        <h6 className="fw-bold text-danger mb-3"><FontAwesomeIcon icon={faTimesCircle} /> Exclusions</h6>
                                        <ul className="list-unstyled mb-0">
                                            {pkg.exclusions?.map((exc, i) => (
                                                <li key={i} className="mb-2 text-muted small d-flex align-items-start">
                                                    <span className="me-2">✗</span> {exc}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN - SIDEBAR */}
                    <div className="col-lg-4">
                        
                        {/* Price Card - Sticky */}
                        <div className="card shadow-sm border-0 mb-4 p-4" style={{ 
                            borderRadius: '12px', 
                            background: '#fff', 
                            position: 'sticky', 
                            top: '80px',
                            zIndex: 1
                        }}>
                            <h5 className="fw-bold mb-3">Package Pricing</h5>
                            
                            {/* Land Only */}
                            <div className="mb-3">
                                <span className="badge bg-light text-dark mb-2 border">Land Only</span>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Per Person (Double)</span>
                                    <h4 className="text-primary fw-bold mb-0">
                                        {pkg.pricing?.currency} {pkg.pricing?.landOnly?.double?.toLocaleString() || "N/A"}
                                    </h4>
                                </div>
                            </div>

                            <hr />

                            {/* With Flight (if applicable) */}
                            {pkg.pricing?.withFlight?.double > 0 && (
                                <div className="mb-3">
                                    <span className="badge bg-light text-dark mb-2 border">With Flight</span>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted small">Per Person (Double)</span>
                                        <h4 className="text-success fw-bold mb-0">
                                            {pkg.pricing?.currency} {pkg.pricing?.withFlight?.double?.toLocaleString()}
                                        </h4>
                                    </div>
                                </div>
                            )}

                            {/* Flight Status */}
                            <div className="mt-3 bg-light p-3 rounded d-flex align-items-center gap-3">
                                <div className={`p-2 rounded-circle ${pkg.flights?.included ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                                    <FontAwesomeIcon icon={faPlane} />
                                </div>
                                <div>
                                    <div className="small fw-bold">Flight Status</div>
                                    <div className="small text-muted">{pkg.flights?.included ? "Included in Package" : "Not Included"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Company Info - Sticky with adjusted top position */}
                        <div className="card shadow-sm border-0 p-4 mt-4" style={{ 
                            borderRadius: '12px',
                            position: 'sticky',
                            top: '420px',
                            zIndex: 1
                        }}>
                            <small className="text-muted text-uppercase fw-bold">Published By</small>
                            <h5 className="fw-bold mt-1 text-dark">{pkg.companyName}</h5>
                            <p className="small text-muted mb-0">Contact the DMC for booking inquiries.</p>
                        </div>

                    </div>
                </div>
            </div>

            {/* Enquiry Modal */}
            {showEnquiryModal && selectedPackage && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white py-2">
                                <h6 className="modal-title">
                                    <i className="bi bi-chat-dots me-2"></i>
                                    Send Enquiry - {selectedPackage.title}
                                </h6>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white"
                                    onClick={closeEnquiryModal}
                                ></button>
                            </div>
                            <form onSubmit={handleEnquirySubmit}>
                                <div className="modal-body">
                                    {/* Package Info Banner */}
                                    <div className="alert alert-info mb-3 py-2">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <i className="bi bi-bag-check fs-5"></i>
                                            </div>
                                            <div className="flex-grow-1 ms-2">
                                                <p className="mb-0 small">
                                                    <strong>{selectedPackage.title}</strong><br/>
                                                    <small>
                                                        {selectedPackage.totalNights} Nights / {selectedPackage.totalDays} Days • 
                                                        {selectedPackage.destinations?.[0]?.countryName || 'Various'}
                                                    </small>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="row g-2">
                                        {/* Travel Dates */}
                                        <div className="col-md-6">
                                            <label className="form-label small">Travel Date *</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                name="checkIn"
                                                value={enquiryData.checkIn}
                                                onChange={handleEnquiryChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small">Return Date *</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                name="checkOut"
                                                value={enquiryData.checkOut}
                                                onChange={handleEnquiryChange}
                                                required
                                            />
                                        </div>
                                        
                                        {/* Nights - Read Only */}
                                        <div className="col-md-4">
                                            <label className="form-label small">Nights *</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm bg-light"
                                                name="nights"
                                                value={enquiryData.nights}
                                                readOnly
                                                disabled
                                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                            />
                                            <small className="text-muted" style={{ fontSize: '10px' }}>Automatically calculated</small>
                                        </div>
                                        
                                        {/* Traveler Configuration */}
                                        <div className="col-md-4">
                                            <label className="form-label small">Adults *</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                name="adults"
                                                value={enquiryData.adults}
                                                onChange={handleNumberChange}
                                                min="1"
                                                max="20"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small">Children</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                name="children"
                                                value={enquiryData.children}
                                                onChange={handleNumberChange}
                                                min="0"
                                                max="20"
                                            />
                                        </div>
                                        
                                        {/* Customer Details */}
                                        <div className="col-md-4">
                                            <label className="form-label small">Your Name *</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                name="customerName"
                                                value={enquiryData.customerName}
                                                onChange={handleEnquiryChange}
                                                placeholder="Enter your name"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small">Email *</label>
                                            <input
                                                type="email"
                                                className="form-control form-control-sm"
                                                name="customerEmail"
                                                value={enquiryData.customerEmail}
                                                onChange={handleEnquiryChange}
                                                placeholder="Enter your email"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small">Phone *</label>
                                            <input
                                                type="tel"
                                                className="form-control form-control-sm"
                                                name="customerPhone"
                                                value={enquiryData.customerPhone}
                                                onChange={handleEnquiryChange}
                                                placeholder="Enter your phone number"
                                                required
                                            />
                                        </div>
                                        
                                        {/* Special Requests */}
                                        <div className="col-12">
                                            <label className="form-label small">Special Requests</label>
                                            <textarea
                                                className="form-control form-control-sm"
                                                name="specialRequests"
                                                value={enquiryData.specialRequests}
                                                onChange={handleEnquiryChange}
                                                placeholder="Any special requests or additional information..."
                                                rows="2"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={closeEnquiryModal}
                                        disabled={isSubmittingEnquiry}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-success btn-sm"
                                        disabled={isSubmittingEnquiry}
                                        style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                                    >
                                        <i className="bi bi-send me-1"></i>
                                        {isSubmittingEnquiry ? "Submitting..." : "Submit Enquiry"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom CSS */}
            <style jsx>{`
                .btn-success {
                    background-color: #28a745;
                    border-color: #28a745;
                }
                
                .btn-success:hover {
                    background-color: #218838;
                    border-color: #1e7e34;
                }
                
                .modal-content {
                    border: none;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
                }
                
                .modal-header {
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                }
                
                .modal-footer {
                    border-top: 1px solid #dee2e6;
                }
                
                @media (max-width: 768px) {
                    .container {
                        padding-left: 10px;
                        padding-right: 10px;
                    }
                    
                    .modal-dialog {
                        margin: 10px;
                    }
                    
                    .modal-body {
                        padding: 15px;
                    }
                    
                    /* Adjust sticky positions for mobile */
                    .col-lg-4 .card {
                        position: relative !important;
                        top: auto !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default DMCPackageDetailsForAgents;